/**
 * The development book — what has been released, what has gone, and is the plan being paid?
 *
 * Two questions, and they are genuinely separate. **Stock**: of everything put on the market, how
 * much has sold, by scheme, by phase and by type. **Money**: of everything buyers agreed to pay,
 * how much has arrived and how much is late. A scheme can be sold out and badly behind on its
 * instalments, and a report that gives one number cannot say so.
 *
 * The words are the developer's own. A scheme selling bare land says "plots", one selling houses
 * says "homes", one selling shop units says "units" — `soldNoun()` in core decides that from
 * `estates.sells` (ADR-0081), and the server sends the answer so the page never guesses.
 *
 * The three stock states are drawn as an **ordered** scale rather than as three categories — sold,
 * then held, then still going — so the sequence reads with the colour removed.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { monthLabel } from "@rentorng/core";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { ColumnChart, CompositionBar, Meter, SEGMENT_COLOURS, SERIES, SegmentKey } from "@/components/charts";
import { GateAlert } from "@/components/padlock";
import { Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, PeriodChoice, ReportHeader, ReportState, usePeriod, useCompactMoney } from "./shared";

interface Sales {
  noun: { one: string; many: string };
  totals: { released: number; sold: number; reserved: number; available: number; withdrawn: number };
  schemes: Array<{
    id: string;
    name: string;
    status: string;
    sells: string;
    released: number;
    sold: number;
    reserved: number;
    available: number;
    soldValue: number;
  }>;
  byPhase: Array<{ estateName: string; phaseName: string; released: number; sold: number; reserved: number; available: number }>;
  byType: Array<{ estateName: string; typeName: string; released: number; sold: number; price: number | null }>;
  plan: { contracted: number; scheduled: number; paid: number; overdue: number; ahead: number };
  sold: Array<{ key: string; count: number; value: number }>;
  months: number;
}

/** How many bars a comparison chart carries before the table is the better instrument. */
const CHART_ROWS = 8;

/**
 * How many rows a table on a report shows before it stops being a report.
 *
 * A development book that has run for years holds dozens of schemes and hundreds of
 * specifications, and a page listing every one of them is a data dump somebody scrolls past. The
 * rows are ordered so the ones worth reading are the ones shown, and the count of what is not
 * shown is printed rather than left to be discovered.
 */
const TABLE_ROWS = 20;

export function SalesReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const [months, setMonths] = usePeriod("months", 12);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-sales", months],
    queryFn: () => api.get<Sales>(`/reports/sales?months=${months}`),
    retry: false,
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Sales and releases" question="How is the book selling, and is the money arriving on plan?" />
        {/* A 402 here means the agency does not hold the development module, and it arrives as a
            `Gate` — the same shape every shut door in this product uses. `GateAlert` draws it and
            says what would open it; anything else falls through to the ordinary failure. */}
        <GateAlert error={error} />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);
  const { noun } = data;

  /*
    The three states, and the one texture that is pinned to a name rather than to a position.

    `Reserved` is hatched **on the column charts**, where every scheme draws all three bars whether
    or not it has anything reserved, so a name-pinned texture is stable there. The composition bar
    below takes no texture from this list at all — it decides its own by drawn position, because it
    omits an empty part and a texture that goes with it. See `alternatingTextures`.
  */
  const stock = [
    { label: "Sold", value: data.totals.sold, colour: SEGMENT_COLOURS.sold },
    { label: "Reserved", value: data.totals.reserved, colour: SEGMENT_COLOURS.reserved, texture: "hatch" as const },
    { label: "Still available", value: data.totals.available, colour: SEGMENT_COLOURS.available },
  ];

  /* Only the schemes that have something on the market. A scheme with nothing released is a
     scheme in planning, and putting it on a comparison of releases adds a bar of nought. */
  const active = data.schemes.filter((scheme) => scheme.released > 0).sort((a, b) => b.released - a.released);
  const chartSchemes = active.slice(0, CHART_ROWS);
  const chartPhases = data.byPhase.filter((row) => row.released > 0).slice(0, CHART_ROWS);

  return (
    <div>
      <ReportHeader
        title="Sales and releases"
        question={`Of the ${noun.many} released, how many have gone — and is the payment plan being kept?`}
        subtitle={`${data.totals.sold} of ${data.totals.released} ${noun.many} sold`}
        actions={
          <PeriodChoice
            label="Over"
            value={months}
            onChange={setMonths}
            options={[
              { value: 6, label: "6m" },
              { value: 12, label: "12m" },
              { value: 24, label: "24m" },
            ]}
          />
        }
      />

      {data.totals.released === 0 ? (
        <Card>
          <EmptyState
            title={`Nothing has been released for sale yet.`}
            hint={`Add ${noun.many} to a scheme and this fills itself in.`}
          />
        </Card>
      ) : (
        <div className="space-y-5">
          <FigureRow>
            <Figure
              label="Released"
              value={data.totals.released}
              sub={`${noun.many} on the market${data.totals.withdrawn ? `, ${data.totals.withdrawn} withdrawn` : ""}`}
            />
            <Figure
              label="Sold"
              value={data.totals.sold}
              tone="good"
              sub={`${Math.round((data.totals.sold / Math.max(1, data.totals.released)) * 100)}% of what was released`}
            />
            <Figure label="Contracted" value={compact(data.plan.contracted)} sub="What buyers agreed to pay" />
            <Figure
              label="Past its due date"
              value={compact(data.plan.overdue)}
              tone={data.plan.overdue > 0 ? "bad" : "good"}
              sub="Instalments due and not settled"
            />
          </FigureRow>

          <div className="grid gap-5 lg:grid-cols-2">
            <ChartCard title={`Where the ${noun.many} stand`} hint="Withdrawn stock is not counted as released, so the three states add up to what is actually on the market.">
              <CompositionBar label={`${noun.many} by state`} segments={stock} format={(value) => String(value)} />
              <SegmentKey segments={stock} format={(value) => String(value)} />
            </ChartCard>

            <ChartCard
              title="The plan against what has arrived"
              hint="Scheduled is every instalment on a live sale; paid is what has been allocated against them. A waived instalment is in neither."
            >
              <Meter
                value={data.plan.paid}
                max={data.plan.scheduled}
                label="Paid against the plan"
                caption={`${full(data.plan.paid)} of ${full(data.plan.scheduled)} scheduled`}
                colour={SERIES[0]}
              />
              <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="eyebrow">Overdue</dt>
                  <dd className="tabular mt-1 font-semibold text-danger-700">{full(data.plan.overdue)}</dd>
                </div>
                <div>
                  <dt className="eyebrow">Paid ahead of time</dt>
                  <dd className="tabular mt-1 font-semibold text-brand-700">{full(data.plan.ahead)}</dd>
                </div>
              </dl>
            </ChartCard>
          </div>

          <ChartCard
            title="By scheme"
            hint={`Sold, reserved and still available, side by side. The ${CHART_ROWS} largest are charted; every one of them is in the table below.`}
          >
            <ColumnChart
              label={`${noun.many} by scheme, split by state`}
              mode="stacked"
              highlight={-1}
              height={240}
              points={chartSchemes.map((scheme) => ({
                label: scheme.name.length > 12 ? `${scheme.name.slice(0, 11)}…` : scheme.name,
                full: scheme.name,
                values: [scheme.sold, scheme.reserved, scheme.available],
              }))}
              series={stock.map((segment) => ({ label: segment.label, colour: segment.colour, texture: segment.texture }))}
              format={(value) => String(Math.round(value))}
              footnote={
                active.length > chartSchemes.length
                  ? `The ${chartSchemes.length} largest of ${active.length} schemes with stock on the market.`
                  : undefined
              }
            />
          </ChartCard>

          {chartPhases.length > 0 ? (
            <ChartCard title="By phase" hint="A scheme with no phases has all its stock under one heading, which is the ordinary case.">
              <ColumnChart
                label={`${noun.many} by phase, split by state`}
                mode="stacked"
                highlight={-1}
                height={240}
                points={chartPhases.map((row) => ({
                  label: row.phaseName.length > 12 ? `${row.phaseName.slice(0, 11)}…` : row.phaseName,
                  full: `${row.estateName} · ${row.phaseName}`,
                  values: [row.sold, row.reserved, row.available],
                }))}
                series={stock.map((segment) => ({ label: segment.label, colour: segment.colour, texture: segment.texture }))}
                format={(value) => String(Math.round(value))}
              />
            </ChartCard>
          ) : null}

          <ChartCard
            title="Sales agreed, month by month"
            hint="Counted on the day the plan starts, which is the date the sale was agreed on rather than the date it was typed in."
          >
            <ColumnChart
              label={`Sales agreed per month over ${data.months} months`}
              height={200}
              highlight={0}
              points={data.sold.map((point) => ({
                label: monthLabel(point.key).split(" ")[0],
                full: monthLabel(point.key),
                values: [point.count],
              }))}
              series={[{ label: "Sales agreed", colour: SERIES[0] }]}
              format={(value) => String(Math.round(value))}
            />
          </ChartCard>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle
                hint={`Most stock on the market first. A scheme in planning with nothing released sits at the bottom.`}
              >
                {data.schemes.length > TABLE_ROWS ? `The ${TABLE_ROWS} busiest of ${data.schemes.length} schemes` : "Every scheme"}
              </CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Scheme</th>
                  <th className="num">Released</th>
                  <th className="num">Sold</th>
                  <th className="num">Reserved</th>
                  <th className="num">Available</th>
                  <th className="num">Sold for</th>
                </tr>
              }
            >
              {data.schemes.slice(0, TABLE_ROWS).map((scheme) => (
                <tr key={scheme.id}>
                  <td>
                    {/* `/app/development/estates/:id` is not a route and never was: the redirect kept is
                      `/app/estates/:id`, at the top level. This answered "page not found". */}
                    <Link to={`/app/development/properties/${scheme.id}`} className="link">
                      {scheme.name}
                    </Link>
                    <span className="block text-xs text-ink-400">{scheme.status}</span>
                  </td>
                  <td className="num tabular">{scheme.released}</td>
                  <td className="num tabular font-medium text-brand-700">{scheme.sold}</td>
                  <td className="num tabular">{scheme.reserved || <span className="text-ink-300">—</span>}</td>
                  <td className="num tabular">{scheme.available}</td>
                  <td className="num tabular">{scheme.soldValue > 0 ? full(scheme.soldValue) : <span className="text-ink-300">—</span>}</td>
                </tr>
              ))}
            </Table>
          </Card>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="Which specifications are moving. A type with a long list released and nothing sold is priced wrong or drawn wrong.">
                {data.byType.length > TABLE_ROWS ? `The ${TABLE_ROWS} best-selling of ${data.byType.length} types` : "By type"}
              </CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Type</th>
                  <th>Scheme</th>
                  <th className="num">Released</th>
                  <th className="num">Sold</th>
                  <th className="num">Asking</th>
                </tr>
              }
            >
              {data.byType.slice(0, TABLE_ROWS).map((row) => (
                <tr key={`${row.estateName}-${row.typeName}`}>
                  <td>{row.typeName}</td>
                  <td className="text-ink-500">{row.estateName}</td>
                  <td className="num tabular">{row.released}</td>
                  <td className="num tabular font-medium">{row.sold}</td>
                  <td className="num tabular">{row.price === null ? <span className="text-ink-300">—</span> : full(row.price)}</td>
                </tr>
              ))}
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
